import React, { useContext, useEffect, useState } from "react";
import { PrismicRichText } from "@prismicio/react";
import { Link, graphql } from "gatsby";
import styled from "styled-components";
import { useWindowScroll, useWindowSize } from "react-use";
import { format, isValid, parseISO } from "date-fns";
import { fr, enGB } from "date-fns/locale";

// Previews
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

// SEO
import { PageSeo } from "../components/global/page-seo";

// Components
import { AspectRatioImageContainer } from "../components/containers/aspect-ratio-image-container";

// Context
import { CurrentLanguage } from "../components/context/current-language";
import { PageColor } from "../components/context/page-color";
import { HeaderColor } from "../components/context/header-color";

const Page = styled.div``;

const Section = styled.section`
  padding: 0 50px;
  margin: 0 0 200px 0;

  & .first-event,
  & .remaining-events {
    grid-column: span 6;
  }

  & .first-event {
    padding: 0 50px;
    margin: 0 0 100px 0;

    @media (max-width: 800px) {
      margin: 0 0 90px 0;
    }

    position: relative;

    & #first-event-container {
      position: sticky;

      top: calc(
        ${(props) => props.height}px - ${(props) => props.heightCalculation}px -
          39px
      );

      @media (max-width: 999px) {
        top: calc(
          ${(props) => props.height}px - ${(props) => props.heightCalculation}px -
            27px
        );
      }
    }

    & .single-event {
      & .text-container {
        padding: 0 39px;

        @media (max-width: 800px) {
          padding: 0 25px;
        }
      }

      & img:not([aria-hidden="true"]) {
        border: 39px solid #fff;
        box-sizing: border-box;

        width: 100%;
        height: 100%;

        @media (max-width: 999px) {
          border: 27px solid #fff;
        }
      }
    }
  }

  & .remaining-events {
    margin: 100px 0 100px 0;
  }

  @media (max-width: 1445px) {
    padding: 0 40px;

    & .grid-2 {
      grid-column-gap: 40px;
    }
  }

  @media (max-width: 999px) {
    padding: 0 30px;
    margin: 0 0 150px 0;

    &.grid-column-gap-50 {
      grid-column-gap: 20px !important;
    }

    & .first-event {
      grid-column: 2 / 9;
      padding: 0 25px;
    }

    & .remaining-events {
      margin: 0 0 150px 0;
      grid-column: 10 / 13;

      &.grid-2 {
        display: block;
        grid-template-columns: unset;
        grid-column-gap: unset;
      }
    }
  }

  @media (max-width: 800px) {
    margin: 0 0 100px 0;
    padding: 0;

    & .first-event {
      grid-column: span 6;
      padding: 0;

      & .single-event {
        max-width: 100%;
        margin: 0;
      }
    }

    & .remaining-events {
      margin: 0 0 100px 0;

      &.grid-2 {
        grid-column: span 6;

        display: flex;
        flex-direction: row;

        max-width: 100%;

        overflow-x: scroll;
        padding: 0 25px;

        &::-webkit-scrollbar {
          display: none;
        }

        & .single-event {
          min-width: 50vw;
          margin: 0 40px 0 0;

          &:last-of-type {
            margin: 0;
          }
        }
      }
    }
  }

  @media (max-width: 500px) {
    & .remaining-events {
      &.grid-2 {
        & .single-event {
          min-width: 70vw;
          margin: 0 25px 0 0;
        }
      }
    }
  }
`;

const Introduction = styled.div`
  padding: 100px 40px 90px 40px;

  @media (max-width: 1445px) {
    padding: 100px 40px;
  }

  @media (max-width: 999px) {
    padding: 31px 25px 150px 25px;
  }

  @media (max-width: 800px) {
    padding: 31px 25px 60px 25px;
  }

  & .title-container {
    margin: 0 0 40px 0;

    @media (max-width: 1445px) {
      margin: 0 0 35px 0;
    }

    & h1 {
      font-size: 70px;
      line-height: 70px;

      @media (max-width: 999px) {
        font-size: 40px;
        line-height: 50px;
      }
    }

    @media (max-width: 800px) {
      margin: 0 0 10px 0;
    }
  }

  & .text-container {
    max-width: 1290px;

    & h3 {
      @media (max-width: 800px) {
        font-size: 19px;
        line-height: 24px;
      }
    }
  }
`;

const EventCard = styled.div`
  & .image-container {
    margin-bottom: 20px;

    & img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  & .text-container {
    margin: 20px 0 0 0;

    & .title-bar {
      display: flex;
      flex-direction: row;
      gap: 4px;
      margin: 0 0 22px 0;

      & p {
        margin: 0;
      }

      & .spacer {
        margin: 0 10px;
      }
    }

    & h3 {
      margin: 20px 0 10px;

      @media (max-width: 999px) {
        font-size: 24px;
        line-height: 32px;
      }
    }

    & .excerpt {
      margin: 10px 0 20px;

      /* Troncature à 3 lignes (issue #59). Les quatre déclarations forment un
         bloc indissociable : \`-webkit-line-clamp\` n'a d'effet que sur un
         \`display: -webkit-box\`. Ce n'est pas le vieux flexbox préfixé qu'on
         trouve dans styles/gallery.css — ne pas le nettoyer comme tel. */
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;

      @media (max-width: 999px) {
        font-size: 14px;
        line-height: 24px;
      }
    }
  }
`;

const AllEvents = ({ data }) => {
  const [currentLanguage, setCurrentLanguage] = useContext(CurrentLanguage);
  const [pageColor, setPageColor] = useContext(PageColor);
  const [headerColor, setHeaderColor] = useContext(HeaderColor);

  useEffect(() => {
    setPageColor(`#efefef`);
    setHeaderColor(`#efefef`);
  }, []);

  const [isSticky, setIsSticky] = useState(false);
  const { x, y } = useWindowScroll();
  const { width, height } = useWindowSize();

  const [heightCalculation, setHeightCalculation] = useState(10);

  const formatEventDate = (dateBegins, dateEnds, labels, lang = "fr-fr") => {
    const locale = lang === "fr-fr" ? fr : enGB;
    const pattern = lang === "fr-fr" ? "dd/MM/yy" : "yy/MM/dd";

    const begins = parseISO(dateBegins);
    const ends = dateEnds ? parseISO(dateEnds) : null;

    if (!isValid(begins)) return null;

    const beginsFormatted = format(begins, pattern, { locale });

    if (!ends || !isValid(ends) || dateBegins === dateEnds) {
      return `${beginsFormatted}`;
    }

    const endsFormatted = format(ends, pattern, { locale });

    return `${labels?.date_from || "from"} ${beginsFormatted} ${
      labels?.date_to || "to"
    } ${endsFormatted}`;
  };

  const labelsEntries = data.prismicSiteLabels?.data?.labels || [];

  const labels = Object.fromEntries(
    labelsEntries
      .filter((item) => item.key && item.value)
      .map((item) => [item.key, item.value])
  );

  const events = data.allPrismicEvent.nodes;

  const categoryTextMap = {
    Paris: "rue de Beaune",
    "New-York": "NYDC",
  };

  const firstEvent =
    events.length > 0 ? (
      <EventCard className="single-event" key={`first_event_${events[0].id}`}>
        <Link to={events[0].url}>
          <AspectRatioImageContainer image={null} padding={140}>
            {events[0]?.data?.featured_img?.url && (
              <img
                src={events[0].data.featured_img.url}
                alt={events[0].data.featured_img.alt || ""}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </AspectRatioImageContainer>
        </Link>

        <div className="text-container">
          <div className="title-bar tag gotham-bold uppercase">
            <p>
              {(() => {
                const categories = events[0].data.categories
                  .map((item) => item.category?.document?.data?.title?.text)
                  .filter(Boolean)
                  .join(", ");
                return (
                  categories || (labels?.offsite ? `${labels.offsite}` : "")
                );
              })()}
            </p>
            <p className="spacer">|</p>
            <p>
              {formatEventDate(
                events[0].data.date_begins,
                events[0].data.date_ends,
                labels,
                events[0].lang
              )}
            </p>
          </div>
          <Link to={events[0].url}>
            <h3>{events[0].data.title?.text}</h3>
          </Link>
          <p className="excerpt">{events[0].data?.excerpt}</p>

          <Link to={events[0].url} className="uppercase small gotham-bold">
            {currentLanguage === `en` ? `See More` : `Voir Plus`}
          </Link>
        </div>
      </EventCard>
    ) : null;

  const remainingEvents = events.slice(1).map((event, index) => (
    <EventCard className="single-event" key={`event_${index}_${event.id}`}>
      <Link to={event.url}>
        <AspectRatioImageContainer image={null} padding={140}>
          {event?.data?.featured_img?.url && (
            <img
              src={event.data.featured_img.url}
              alt={event.data.featured_img.alt || ""}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          )}
        </AspectRatioImageContainer>
      </Link>

      <div className="text-container">
        <div className="title-bar tag gotham-bold uppercase">
          <p>
            {(() => {
              const categories = event.data.categories
                .map((item) => {
                  const categoryName =
                    item.category?.document?.data?.title?.text;
                  return categoryName
                    ? categoryTextMap[categoryName] || categoryName
                    : null;
                })
                .filter(Boolean)
                .join(", ");
              return categories || (labels?.offsite ? `${labels.offsite}` : "");
            })()}
          </p>
          <p className="spacer">|</p>
          <p>
            {formatEventDate(
              event.data.date_begins,
              event.data.date_ends,
              labels,
              event.lang
            )}
          </p>
        </div>
        <Link to={event.url}>
          <h3>{event.data.title?.text}</h3>
        </Link>
        <p className="excerpt">{event.data?.excerpt}</p>

        <Link to={event.url} className="uppercase small gotham-bold">
          {currentLanguage === `en` ? `See More` : `Voir Plus`}
        </Link>
      </div>
    </EventCard>
  ));

  useEffect(() => {
    let firstEventContainer = document.getElementById(`first-event-container`);
    if (firstEventContainer) {
      let eventContainerDims = firstEventContainer.getBoundingClientRect();
      setHeightCalculation(eventContainerDims.height);
    }
  }, [height, width, setHeightCalculation]);

  const pageTitle =
    data.prismicEventspage?.data?.title?.text ||
    (currentLanguage === "en" ? "All Events" : "Tous les événements");

  return (
    <>
      <PageSeo
        title={pageTitle}
        image={null}
        description={null}
        url={
          typeof window !== "undefined"
            ? window.location.href
            : `/${currentLanguage}/events/`
        }
      />
      <Page className="page">
        <Introduction>
          <div className="title-container">
            <h1>{pageTitle}</h1>
          </div>
          <div className="text-container">
            <PrismicRichText
              field={data.prismicEventspage.data.text.richText}
              components={{
                paragraph: ({ children }) => <h3>{children}</h3>,
              }}
            />
          </div>
        </Introduction>
        <Section
          className="events-container grid-12 grid-column-gap-50"
          height={height}
          heightCalculation={heightCalculation}
        >
          {firstEvent && (
            <div className={`first-event ${isSticky ? `sticky` : ``}`}>
              <div id="first-event-container">{firstEvent}</div>
            </div>
          )}

          {remainingEvents.length > 0 && (
            <div className="remaining-events grid-2">{remainingEvents}</div>
          )}
        </Section>
      </Page>
    </>
  );
};

export default withPrismicPreview(AllEvents);

export const query = graphql`
  query AllEventsQuery($lang: String) {
    allPrismicEvent(
      filter: { lang: { eq: $lang } }
      sort: { data: { date_begins: DESC } }
    ) {
      nodes {
        _previewable
        id
        uid
        url
        lang
        data {
          title {
            text
          }
          excerpt
          date_begins
          date_ends
          featured_img {
            url
            alt
          }
          categories {
            category {
              uid
              document {
                ... on PrismicGalleriesSingle {
                  data {
                    title {
                      text
                    }
                  }
                  uid
                  url
                  lang
                }
              }
            }
          }
        }
      }
    }
    prismicEventspage(lang: { eq: $lang }) {
      data {
        title {
          text
        }
        text {
          richText
        }
      }
    }

    prismicSiteLabels(uid: { eq: "default" }, lang: { eq: $lang }) {
      data {
        labels {
          key
          value
        }
      }
    }
  }
`;
